import { Stack } from '@fluentui/react';
import { Button, buttonStylesPrimary, PageHeader } from '@h2oai/ui-kit';
import { useEffect, useRef } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation, useParams } from 'react-router-dom';

import { useQueryWorkspace } from '../../authz/authz.hooks';
import { GrpcStatusCode, isGrpcGatewayErrorResponse } from '../../services/HTTPResponse';
import { defaultPagePadding, stackStylesNoLeftNav } from '../../themes/themes';
import { useCloudPlatformDiscovery } from '../../utils/hooks';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { RoutePaths } from '../Routes';
import { DriveContent } from './components';
import { ensureTrailingSlash, isDirectory } from './helpers';
import { useAutoConnection, useBreadcrumbs, useSmoothNavigation } from './hooks';

const LoadingScreen = () => (
  <Stack
    styles={{ root: { padding: defaultPagePadding, flexGrow: 1, justifyContent: 'center', alignItems: 'center' } }}
  >
    <div>Loading workspace...</div>
  </Stack>
);

const AccessDeniedScreen = ({ onBackToDrive }: { onBackToDrive: () => void }) => (
  <Stack
    styles={{ root: { padding: defaultPagePadding, flexGrow: 1, justifyContent: 'center', alignItems: 'center' } }}
  >
    <Stack tokens={{ childrenGap: 16 }} horizontalAlign="center">
      <div style={{ fontSize: '18px', fontWeight: 600 }}>Access Denied</div>
      <div>You don&apos;t have access to the requested workspace.</div>
      <Button text="Back to Drive" styles={buttonStylesPrimary} onClick={onBackToDrive} />
    </Stack>
  </Stack>
);

const DriveWorkspaceContent = () => {
  const location = useLocation();
  const { bucketName } = useParams<{ bucketName: string }>();
  const cloudDiscovery = useCloudPlatformDiscovery();
  const { ACTIVE_WORKSPACE_NAME, activeWorkspaceInitialized, activeWorkspace } = useWorkspaces();

  const pathParts = location.pathname.split('/').filter(Boolean);
  const pathWithoutTrailingSlash = pathParts.slice(2).join('/') || '';

  const fullPath =
    isDirectory(location.pathname) && pathWithoutTrailingSlash
      ? `${pathWithoutTrailingSlash}/`
      : pathWithoutTrailingSlash;

  const isViewingFile = fullPath && !isDirectory(fullPath);
  const directoryPath = isViewingFile ? fullPath.substring(0, fullPath.lastIndexOf('/')) : fullPath;

  // Initialize S3 service and navigation
  const { s3Service, isAutoConnecting, autoConnectionError } = useAutoConnection({
    activeWorkspaceInitialized,
    stsDriveUrl: cloudDiscovery?.stsDriveUrl,
    workspaceDriveUrl: cloudDiscovery?.workspaceDriveUrl,
    activeWorkspaceName: ACTIVE_WORKSPACE_NAME,
  });

  const { navigateToDirectory, isNavigating } = useSmoothNavigation({ bucketName, s3Service });

  const { breadcrumbItems } = useBreadcrumbs({
    bucketName,
    directoryPath,
    activeWorkspace,
    onNavigate: navigateToDirectory,
  });
  const lastDirectoryPathRef = useRef<string>('');
  useEffect(() => {
    if (s3Service?.isConnected) {
      lastDirectoryPathRef.current = directoryPath;
      const prefix = directoryPath ? ensureTrailingSlash(directoryPath) : undefined;
      void s3Service.listObjects(prefix);
    }
  }, [s3Service?.isConnected, directoryPath]);

  return (
    <Stack
      styles={{
        ...stackStylesNoLeftNav,
        root: { ...(stackStylesNoLeftNav.root as any), flexGrow: 1 },
      }}
    >
      <Stack styles={{ root: { padding: '0 0 30px' } }}>
        <PageHeader breadcrumbProps={{ items: breadcrumbItems }} pageTitle="H2O Drive" />
      </Stack>

      <Stack styles={{ root: { background: '#fff', padding: 16, borderRadius: 8, flexGrow: 1 } }}>
        <DriveContent
          activeWorkspaceInitialized={activeWorkspaceInitialized}
          isAutoConnecting={isAutoConnecting}
          autoConnectionError={autoConnectionError}
          s3Service={s3Service}
          isNavigating={isNavigating}
          onDirectoryClick={navigateToDirectory}
          currentPath={directoryPath}
          stsDriveUrl={cloudDiscovery?.stsDriveUrl}
          workspaceDriveUrl={cloudDiscovery?.workspaceDriveUrl}
        />
      </Stack>
    </Stack>
  );
};

const DriveWithWorkspaceCheck = () => {
  const { bucketName } = useParams<{ bucketName: string }>();
  const history = useHistory();
  const location = useLocation();
  const { ACTIVE_WORKSPACE_NAME, activeWorkspaceInitialized, activateWorkspace } = useWorkspaces();
  const previousWorkspaceRef = useRef<string | undefined>(ACTIVE_WORKSPACE_NAME);

  const currentWorkspaceUuid = ACTIVE_WORKSPACE_NAME?.replace('workspaces/', '');
  const isCurrentWorkspace = currentWorkspaceUuid === bucketName;

  // Determine if workspace changed via dropdown or URL navigation
  const workspaceChangedViaDropdown =
    previousWorkspaceRef.current &&
    ACTIVE_WORKSPACE_NAME !== previousWorkspaceRef.current &&
    activeWorkspaceInitialized;

  // Query the workspace from URL to check if user has access
  // Only enabled when URL has different workspace AND workspace didn't just change via dropdown
  const targetWorkspaceQuery = useQueryWorkspace(bucketName, {
    enabled: !!bucketName && activeWorkspaceInitialized && !isCurrentWorkspace && !workspaceChangedViaDropdown,
    retry: false,
  });

  // Update previousWorkspaceRef
  useEffect(() => {
    previousWorkspaceRef.current = ACTIVE_WORKSPACE_NAME;
  }, [ACTIVE_WORKSPACE_NAME]);

  // Case 1: isCurrentWorkspace is true - render the Drive
  if (isCurrentWorkspace) return <DriveWorkspaceContent />;

  // Case 2: !bucketName - redirect to DriveBucket URL (handled by parent Drive component)

  // Case 3: bucketName !== currentWorkspaceUuid
  if (bucketName && currentWorkspaceUuid && bucketName !== currentWorkspaceUuid) {
    // Case 3a: ACTIVE_WORKSPACE_NAME !== previousWorkspaceRef - redirect to DriveBucket URL with updated workspaceId
    if (workspaceChangedViaDropdown) {
      const pathParts = location.pathname.split('/').filter(Boolean);
      const pathAfterBucket = pathParts.slice(2).join('/');
      const pathSuffix = pathAfterBucket ? `/${pathAfterBucket}` : '';
      history.replace(`${RoutePaths.DRIVE}/${currentWorkspaceUuid}${pathSuffix}`);
      return null; // Will re-render with new URL
    }

    // Case 3b: ACTIVE_WORKSPACE_NAME === previousWorkspaceRef - fetch the targetworkspace
    if (targetWorkspaceQuery) {
      // Error? - Show the message
      if (targetWorkspaceQuery.isError) {
        const error = targetWorkspaceQuery.error;
        const isPermissionError =
          isGrpcGatewayErrorResponse(error) &&
          (error.code === GrpcStatusCode.PERMISSION_DENIED || error.code === GrpcStatusCode.NOT_FOUND);

        if (isPermissionError) {
          return (
            <AccessDeniedScreen onBackToDrive={() => history.replace(`${RoutePaths.DRIVE}/${currentWorkspaceUuid}`)} />
          );
        }
      }

      // Success? - activateWorkspace(bucketName)
      if (targetWorkspaceQuery.isSuccess && targetWorkspaceQuery.data) {
        activateWorkspace(`workspaces/${bucketName}`);
        return <LoadingScreen />;
      }

      // Loading
      if (targetWorkspaceQuery.isLoading) return <LoadingScreen />;
    }
  }

  // Fallback - render content
  return <DriveWorkspaceContent />;
};

export const Drive = () => {
  const { ACTIVE_WORKSPACE_NAME, activeWorkspaceInitialized } = useWorkspaces();

  const getWorkspaceUuid = () => {
    if (!ACTIVE_WORKSPACE_NAME) return '';
    return ACTIVE_WORKSPACE_NAME.replace('workspaces/', '');
  };

  return (
    <Switch>
      <Route exact path={RoutePaths.DRIVE}>
        {activeWorkspaceInitialized && ACTIVE_WORKSPACE_NAME ? (
          <Redirect to={`${RoutePaths.DRIVE}/${getWorkspaceUuid()}`} />
        ) : (
          <Stack
            styles={{
              root: { padding: defaultPagePadding, flexGrow: 1, justifyContent: 'center', alignItems: 'center' },
            }}
          >
            <div>Loading workspace...</div>
          </Stack>
        )}
      </Route>

      <Route path={RoutePaths.DRIVE_BUCKET}>
        <DriveWithWorkspaceCheck />
      </Route>
    </Switch>
  );
};
